import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { TenantRequest } from '@app/core/models/tenant/tenant-request';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { TenantService } from '@services/http/tenant.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { MenuItem } from 'primeng/api';

@Component({
  templateUrl: './tenant-register.component.html',
  styleUrls: ['./tenant-register.component.scss'],
  standalone: false,
})
export class TenantRegisterComponent extends VaultBaseComponent implements OnInit {
  defaultLanguage: string = 'pt-BR';
  tenantId: string = '';
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  tenantPermissions = Permissions.Tenant;
  selectedStatus: Option | null = {
    label: this.t(this.tk.User.Active),
    value: '1',
  };

  public tenantRegisterProps = nameof<TenantRequest>();

  tenantForm = this.formBuilder.group({
    [this.tenantRegisterProps.id]: [{ value: '', disabled: true }],
    [this.tenantRegisterProps.name]: ['', [Validators.required]],
    [this.tenantRegisterProps.status]: [''],
    [this.tenantRegisterProps.active]: [true],
    [this.tenantRegisterProps.description]: [''],
  });

  userStatusOptions: Option[] = [
    { label: this.t(this.tk.User.Active), value: '1' },
    { label: this.t(this.tk.User.Inactive), value: '0' },
  ];

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private formErrorHandler: FormErrorHandlerService,
    private toast: ToastHelperService,
    private tenantService: TenantService
  ) {
    super(translateService);
    this.defaultLanguage = translateService.currentLang;
  }

  ngOnInit(): void {
    this.breadcrumbs = [
      { label: this.t(this.tk.General.Administration), routerLink: '/' },
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/dashboard/tenant/list',
      },
      {
        label: this.t(this.tk.Management.Tenant),
        routerLink: '/dashboard/tenant/register',
      },
    ];

    this.getVerifyTenantId();
  }

  getVerifyTenantId(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.tenantId = params['id'];
        this.utilsService.setIsLoading(true);
        this.tenantService.getTenantById(this.tenantId).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            if (response) {
              const tenant = response;

              this.tenantForm.controls[this.tenantRegisterProps.id].setValue(tenant.id || '');

              this.tenantForm.controls[this.tenantRegisterProps.name].setValue(tenant.name || '');

              this.tenantForm.controls[this.tenantRegisterProps.status].setValue(
                tenant.active || ''
              );

              this.selectedStatus =
                this.userStatusOptions.find(option => !!Number(option.value) == tenant.active) ||
                null;
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.log('getTenantById error: ', error);
            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingTenant),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      }
    });
  }

  handleOptionChange(selectedOptions: Option | null, key: string): void {
    switch (key) {
      case 'selectedStatus':
        this.selectedStatus = selectedOptions;
        this.tenantForm.controls[this.tenantRegisterProps.status].setValue(
          selectedOptions?.value || ''
        );
        break;
    }
  }

  cancelRegister(): void {
    this.router.navigate(['dashboard/tenant/list']);
  }

  registerTenant(): void {
    if (this.tenantForm.invalid) {
      this.tenantForm.markAllAsTouched();
      return;
    }

    const formValue = this.tenantForm.getRawValue() as unknown as TenantRequest;

    if (this.tenantId) {
      this.editTenant(formValue);
    } else {
      this.postTenant(formValue);
    }
  }

  postTenant(formValue: TenantRequest): void {
    const keysToSendPOST = ['name'];
    const formToSend = this.getTenantToSend(formValue, keysToSendPOST);
    this.utilsService.setIsLoading(true);
    this.tenantService.postTenant(formToSend).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess(
          this.t(this.tk.Management.TenantCreatedSuccessfully),
          response?.message || ''
        );
        this.router.navigate(['dashboard/tenant/list']);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.log('postTenant error: ', error);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.tenantForm, error);
        } else {
          console.log('postPermissionGroup error: ', error);
        }

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorCreatingTenant),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  editTenant(formValue: TenantRequest): void {
    const keysToSendPUT = ['id', 'name', 'active'];
    const formToSend = this.getTenantToSend(formValue, keysToSendPUT);
    this.utilsService.setIsLoading(true);
    this.tenantService.putTenant(formToSend).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess(
          this.t(this.tk.Management.TenantUpdatedSuccessfully),
          response?.message || ''
        );
        this.router.navigate(['dashboard/tenant/list']);
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.tenantForm, error);
        } else {
          console.log('putPermissionGroup error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorUpdatingTenant),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getTenantToSend(formValue: TenantRequest, keysToSend: string[]): TenantRequest {
    const form = { ...formValue };
    Object.keys(form).forEach(key => {
      if (!keysToSend.includes(key)) {
        delete form[key as keyof TenantRequest];
      }
    });
    return form;
  }
}
